/**
 * LocationPicker — searchable country → state → city cascade.
 * Reused by CreateUserForm and EditUserForm.
 */

import { FormField } from '../../components/form-field';
import { useTranslation } from 'react-i18next';
import { useCountries, useStates, useCities } from '../../hooks/use-location-data';
import { SearchableSelect } from '../../components/searchable-select';

interface LocationPickerProps {
  country: string;
  state: string;
  city: string;
  onCountryChange: (value: string) => void;
  onStateChange: (value: string) => void;
  onCityChange: (value: string) => void;
}

export function LocationPicker({
  country,
  state,
  city,
  onCountryChange,
  onStateChange,
  onCityChange,
}: LocationPickerProps) {
  const { t } = useTranslation();

  const { countries, isLoading: loadingCountries } = useCountries();
  const { states, isLoading: loadingStates } = useStates(country);
  const { cities, isLoading: loadingCities } = useCities(country, state);

  const countryOptions = countries.map((c) => ({ value: c.name, label: c.name }));
  const stateOptions   = states.map((s) => ({ value: s, label: s }));
  const cityOptions    = cities.map((c) => ({ value: c, label: c }));

  const handleCountryChange = (value: string) => {
    onCountryChange(value);
    onStateChange('');
    onCityChange('');
  };

  const handleStateChange = (value: string) => {
    onStateChange(value);
    onCityChange('');
  };

  return (
    <>
      {/* Country */}
      <FormField label={t('admin.users.form.country')}>
          {(field) => (
            <SearchableSelect
              id={field.id}
              options={countryOptions}
              value={country}
              onChange={handleCountryChange}
              placeholder={t('admin.users.form.selectCountry')}
              searchPlaceholder={t('admin.users.form.searchCountry')}
              isLoading={loadingCountries}
            />
          )}
        </FormField>

      {/* State */}
      <FormField
        label={t('admin.users.form.state')}
        hint={!country ? t('admin.users.form.selectCountryFirst') : undefined}
      >
          {(field) => (
            <SearchableSelect
              id={field.id}
              options={stateOptions}
              value={state}
              onChange={handleStateChange}
              placeholder={t('admin.users.form.selectState')}
          searchPlaceholder={t('admin.users.form.searchState')}
          isLoading={loadingStates}
          disabled={!country}
            />
          )}
        </FormField>

      {/* City */}
      <FormField
        label={t('admin.users.form.city')}
        hint={!state ? t('admin.users.form.selectStateFirst') : undefined}
      >
          {(field) => (
            <SearchableSelect
              id={field.id}
              options={cityOptions}
              value={city}
              onChange={onCityChange}
              placeholder={t('admin.users.form.selectCity')}
          searchPlaceholder={t('admin.users.form.searchCity')}
          isLoading={loadingCities}
          disabled={!state}
            />
          )}
        </FormField>
    </>
  );
}
